/**
 * PWA-Live-Update: laedt die App beim Zurueckkehren aus dem Hintergrund neu,
 * sobald ein neues Deploy vorliegt.
 *
 * Hintergrund: iOS friert eine Homescreen-PWA beim Verlassen ein und setzt den
 * eingefrorenen Zustand beim Zurueckkehren fort — `index.html` wird NICHT neu
 * geladen, ein Deploy bleibt bis zum echten App-Neustart unsichtbar. Loesung:
 * die laufende App vergleicht ihre eingebackene Build-Kennung (`__APP_VERSION__`)
 * mit der ausgelieferten `/version.json`; weichen sie ab, gibt es eine neue
 * Version -> stiller Reload.
 *
 * Bewusst NUR beim Zurueckkehren (visibilitychange -> sichtbar / focus), nicht
 * im aktiven Vordergrund — so wird niemand mitten im Tippen unterbrochen
 * (Deploys passieren ohnehin nicht, waehrend jemand aktiv tippt).
 */

const VERSION_URL = '/version.json';
/** sessionStorage-Schluessel: pro Remote-Version hoechstens einmal reloaden. */
const RELOAD_GUARD_KEY = 'hoseaus.reloadedForVersion';

let inFlight = false;

async function fetchRemoteVersion(): Promise<string | null> {
  try {
    // no-store + Cache-Buster: nie aus dem HTTP-Cache, immer der frische Wert.
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data: unknown = await res.json();
    const version = (data as { version?: unknown } | null)?.version;
    return typeof version === 'string' ? version : null;
  } catch {
    // Offline / dev (keine version.json) -> einfach nichts tun.
    return null;
  }
}

async function checkForUpdate(): Promise<void> {
  if (inFlight) return;
  inFlight = true;
  try {
    const remote = await fetchRemoteVersion();
    if (remote === null || remote === __APP_VERSION__) return;

    // Schleifen-Schutz: fuer genau diese Remote-Version nur einmal neu laden.
    // Sollte die neue Version nach dem Reload (z.B. wegen Propagationsverzug auf
    // dem Server) noch nicht ausgeliefert werden, gibt es kein Reload-Karussell.
    if (sessionStorage.getItem(RELOAD_GUARD_KEY) === remote) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, remote);

    window.location.reload();
  } finally {
    inFlight = false;
  }
}

/**
 * Registriert die Update-Pruefung. Idempotent genug fuer den einmaligen Aufruf
 * beim App-Start (main.tsx).
 */
export function initAppUpdateCheck(): void {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForUpdate();
  });
  // Fallback fuer Umgebungen, in denen focus statt visibilitychange feuert.
  window.addEventListener('focus', () => void checkForUpdate());
}
